<template>
  <div ref="root">
    <ui-banner v-if="zdTrialVisible" status="info" @close="dismissZDTrialBanner">
      <i18n-t tag="div" keypath="modules.application.banners.trial_left.template_zd_only">
        <template #days>
          <a v-if="hasAccountRole('ADMIN')" href="/admin" target="_blank" rel="noopener noreferrer">
            <strong>{{ $t('modules.application.banners.trial_left.days', trialRemaining) }}</strong>
          </a>
          <strong v-else>{{ $t('modules.application.banners.trial_left.days', trialRemaining) }}</strong>
        </template>
      </i18n-t>
    </ui-banner>

    <ui-banner v-if="trialEndingVisible" status="info" stubborn>
      <p v-sanitize="trialRemainingText" />
    </ui-banner>

    <ui-banner v-if="userLimitVisible" status="warning" @close="dismissUserLimitBanner">
      <i18n-t keypath="modules.application.banners.user_limit.template">
        <template #link>
          <router-link :to="{ name: 'settings.subscription' }">
            {{ $t('modules.application.banners.user_limit.link') }}
          </router-link>
        </template>
      </i18n-t>
    </ui-banner>

    <ui-banner v-if="newNavAnnouncementVisible" status="info" @close="dismissGlobalNavAnnouncementBanner">
      <i18n-t keypath="modules.application.banners.new_navigation.eap_launch.template">
        <template #intro>
          <span class="inline-flex items-center gap-1">
            <info-icon class="icon-s" />
            <strong>{{ $t('modules.application.banners.new_navigation.eap_launch.intro') }} </strong>
          </span>
        </template>
        <template #link>
          <a
            :href="$t('modules.application.banners.new_navigation.article_url')"
            rel="noopener noreferrer"
            target="_blank"
          >
            {{ $t('modules.application.banners.new_navigation.link_button') }}
            <external-link-icon class="icon-s" />
          </a>
        </template>
      </i18n-t>
    </ui-banner>

    <ui-banner v-if="newNavReleaseReminderVisible" status="info" @close="dismissGlobalNavReleaseReminderBanner">
      <i18n-t keypath="modules.application.banners.new_navigation.release_reminder.template">
        <template #intro>
          <span class="inline-flex items-center gap-1">
            <info-icon class="icon-s" />
            <strong>{{ $t('modules.application.banners.new_navigation.release_reminder.intro') }} </strong>
          </span>
        </template>
        <template #link>
          <a
            :href="$t('modules.application.banners.new_navigation.article_url')"
            rel="noopener noreferrer"
            target="_blank"
          >
            {{ $t('modules.application.banners.new_navigation.link_button') }}
            <external-link-icon class="icon-s" />
          </a>
        </template>
      </i18n-t>
    </ui-banner>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import UiBanner from '@klausapp/ui-kit/components/UiBanner.vue'
import { ExternalLinkIcon, InfoIcon } from '@klausapp/ui-kit/icons'

import { session } from '@/composables/useSession'
import { hasAccountRole } from '@/utils/roleUtils'
import analytics from '@/utils/analytics'
import { useEvent } from '@/composables/useEvent'
import useApplicationBanners from '../composables/useApplicationBanners'

const emit = defineEmits<{
  (e: 'padding-change', padding: string): void
}>()

const GLOBAL_NAV_ANNOUNCEMENT_BANNER_SHOWN_KEY = 'globalNavAnnouncementBannerShown'
const GLOBAL_NAV_GA_RELEASE_REMINDER_SHOWN_KEY = 'globalNavGaReleaseReminderShown'

const getNewNavAnnouncementVisibility = () => {
  if (!session.features.zendeskUiAnnouncementBanner || hasAccountRole('ADMIN')) return false

  // Check if user has previously dismissed the banner
  const bannerDismissed = localStorage.getItem(GLOBAL_NAV_ANNOUNCEMENT_BANNER_SHOWN_KEY) === 'true'
  return !bannerDismissed
}

const getNewNavReleaseReminderVisibility = () => {
  if (session.features.zendeskUiNavigation || !session.features.zendeskUiReleaseReminderBanner) return false

  // Check if user has previously dismissed the banner
  const bannerDismissed = localStorage.getItem(GLOBAL_NAV_GA_RELEASE_REMINDER_SHOWN_KEY) === 'true'
  return !bannerDismissed
}

const root = ref<HTMLDivElement>()
const newNavAnnouncementVisible = ref(getNewNavAnnouncementVisibility())
const newNavReleaseReminderVisible = ref(getNewNavReleaseReminderVisibility())

const {
  trialEndingVisible,
  trialRemainingCount: trialRemaining,
  trialRemainingText,
  userLimitVisible,
  zdTrialVisible,
  hideZDTrialBanner,
} = useApplicationBanners()

const bannersVisible = computed(
  () =>
    zdTrialVisible.value ||
    trialEndingVisible.value ||
    userLimitVisible.value ||
    newNavAnnouncementVisible.value ||
    newNavReleaseReminderVisible.value,
)

watch(bannersVisible, async () => {
  await nextTick()
  setBannerPadding()
})

useEvent('user-limit-reached', () => (userLimitVisible.value = true))

onMounted(async () => {
  await nextTick()
  setBannerPadding()
})

const dismissZDTrialBanner = () => {
  hideZDTrialBanner.value = true
  analytics.closeMessageClicked()
}

const dismissUserLimitBanner = () => {
  userLimitVisible.value = false
  analytics.closeMessageClicked()
}

const dismissGlobalNavAnnouncementBanner = () => {
  localStorage.setItem(GLOBAL_NAV_ANNOUNCEMENT_BANNER_SHOWN_KEY, 'true')
  newNavAnnouncementVisible.value = getNewNavAnnouncementVisibility()
  analytics.closeMessageClicked()
}

const dismissGlobalNavReleaseReminderBanner = () => {
  localStorage.setItem(GLOBAL_NAV_GA_RELEASE_REMINDER_SHOWN_KEY, 'true')
  newNavReleaseReminderVisible.value = getNewNavReleaseReminderVisibility()
  analytics.closeMessageClicked()
}

const setBannerPadding = () => {
  let padding = bannersVisible.value ? '45px' : '0px'

  if (document.body.clientWidth <= 768 && root.value) {
    // Get height of all banners
    const height = Array.from(root.value.children).reduce((acc, child) => {
      if ((child as HTMLElement).style.display === 'none') return acc
      return acc + (child as HTMLElement).offsetHeight
    }, 0)

    padding = height + 'px'
  }

  emit('padding-change', padding)
}
</script>
